import React  from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLivePreview } from '@payloadcms/live-preview-react';

import {
    Box,
    Container,
    H1,
    Grid,
} from '@saladbob/sassafras'

import Image from 'next/image';

import getSite from '../../crud/getSite';
import getPostById from '../../crud/getPostById';
import Blocks from '../../components/blocks/Blocks';
import YouTube from '../../components/media/YouTube';
import VideoEmbed from '../../components/media/VideoEmbed';
import HostTag from '../../components/tags/HostTag';
import TimeTag from '../../components/tags/TimeTag';
import TournamentTag from '../../components/tags/TournamentTag';
import { useEnv } from '../../components/providers/EnvProvider';

import Layout from '../../components/Layout';
import type { NextPageWithLayout, PageProps } from '../_app';
import Post from '../../types/Post';

type Props = {
    post: Post,
    Header: React.FC,
    payloadUrl: string,
};

function useParallax(value, distance) {
    return useTransform(value, [0, 1], [0, distance]);
}

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking', // can also be true or 'blocking'
    }
}

export async function getStaticProps({ params }) {
    if (!params.id || params.id === 'undefined') {
        return {
            notFound: true,
        }
    }

    const site = await getSite();
    const post = await getPostById(process.env.NEXT_PUBLIC_API_URL, params.id);

    if (!post) {
        return {
            notFound: true,
        }
    }

    return {
        props: {
            metaTitle: post.title,
            post,
            ...site,
        },
    }
}

const PostHeader: React.FC<Post> = ({
    title,
    featuredImage,
    videoEmbed,
    youtube,
    createdAt,
    tournaments,
    hosts,
}) => {
    const { headerHeight } = useEnv();
    const { scrollYProgress } = useScroll();
    const y = useParallax(scrollYProgress, 50);

    if (youtube) {
        return (
            <Box padding="lg" minHeight={`calc(100vh - ${headerHeight}px)`} verticalAlign="center" bgColor="secondary800">
                <Container size="lg">
                    <H1 align="center" color="white" mb="xl">{title}</H1>
                    <YouTube title={title} videoId={youtube} depth="md" />
                </Container>
            </Box>
        )
    }

    if (videoEmbed) {
        return (<VideoEmbed html={videoEmbed} />)
    }

    if (featuredImage) {
        return (<Container size="stretch">
            <Grid
                columns={{ phone: '1fr', laptop: '3fr 2fr' }}
                rows={{ phone: '240px min-content', laptop: '1fr' }}
            >
                <Box overflow="hidden" width="100%" height="100%" display="flex">
                    <motion.div style={{ y, scale: 1.3, width: '100%', height: '100%' }}>
                        <Box
                            width="100%"
                            height="100%"
                            bgColor={featuredImage.color}
                            bgImage={featuredImage.sizes.blur.url}
                            pb={{ phone: '240px', laptop: '60%' }}
                        >
                            <Image
                                src={featuredImage.sizes.hd.url}
                                loading="lazy"
                                //placeholder={featuredImage.blurDataUrl ? 'blur' : 'empty'}
                                //blurDataURL={featuredImage.blurDataUrl}
                                fill={true}
                                alt={title}
                                style={{
                                    objectFit: 'cover',
                                }}
                            />
                        </Box>
                    </motion.div>
                </Box>
                <Box
                    width="100%"
                    bgColor="tertiary300"
                    padding="lg"
                    verticalAlign="center"
                >
                    <motion.div style={{ y }}>
                        <Box bgColor="tertiary" padding="sm" ms={{ phone: '0', laptop: '-64px'}}>
                            {(tournaments || hosts) && 
                                <Box mb="xs" align="center">
                                    {tournaments && tournaments.map((tournament) => (
                                        <TournamentTag tournament={tournament} />
                                    ))}
                                    {hosts && hosts.map((host) => (
                                        <HostTag host={host} font="small" />
                                    ))}
                                </Box>
                            }
                            <H1 font={{ phone: 'h4', laptop: 'h1', desktop: 'jumbo' }} mb="md">
                                {title}
                            </H1>
                            <TimeTag date={createdAt} />
                        </Box>
                    </motion.div>
                </Box>
            </Grid>
        </Container>)
    }
    return null;
}

const Page: NextPageWithLayout<Props> = ({ post }) => {
    return (<>
        <Box bgColor="neutral50">
            <PostHeader {...post} />
            <Container size="lg">
                {post.layout && post.layout.length > 0 && (
                    <Box padding={{ phone: '0', laptop: ['lg', 0] }} bgColor="white" mt={{ phone: '0', laptop: !post.youtube && !post.videoEmbed ? '-64px' : '0' }}>
                        <Blocks blocks={post.layout} />
                    </Box>
                )}
            </Container>
        </Box>
    </>);
};

Page.getLayout = function getLayout(page: React.ReactElement, pageProps: PageProps ) {
    return (
        <Layout { ...pageProps }>
            {page}
        </Layout>
    )
}

export default Page;